/**
 * Browser-side automation detection, evaluated before analytics dispatch.
 *
 * Ekho's SDK and chat widget install on third-party dealer sites and emit on
 * every host page load, so every JS-executing crawler on those sites becomes a
 * tracked identity in every CDP the surfaces write to. This module decides,
 * once per page load, whether the current runtime is automation.
 *
 * Detection reads only what the client volunteers about itself. There is no
 * canvas, WebGL, or font probing: fingerprinting an embedded guest on someone
 * else's site is both a privacy problem and a bundle-size one, and the actors
 * that dominate our traffic declare themselves without it.
 *
 * Three signals, cheapest and most reliable first:
 *
 * 1. `navigator.userAgentData.brands` naming a headless build. Chromium reports
 *    `HeadlessChrome` here when run headless, and this is the single largest
 *    signal in our data by a wide margin.
 * 2. A user-agent string naming a crawler or automation tool. The conventional
 *    channel, and the one every well-behaved crawler uses.
 * 3. `navigator.webdriver`, set by WebDriver-controlled browsers.
 *
 * All three are spoofable, and stealth plugins patch `userAgentData` and
 * `webdriver` specifically. That is an accepted limit: the goal is to stop
 * paying for automation that does not hide, not to defeat automation that does.
 * Traffic that evades all three is caught retrospectively in the warehouse by
 * identity-persistence and engagement analysis, which no client-side check can
 * replicate.
 *
 * Deliberately NOT a signal: the presence or absence of Client Hints. Absent
 * hints is the classic headless tell, but our largest headless actor sends a
 * complete set, so treating absence as suspicious would miss it entirely while
 * penalising older real browsers.
 */
/**
 * Crawler and automation tokens matched against the user-agent string, lowercased.
 *
 * Split into two groups because the tradeoffs differ. Vendor names are precise
 * and safe. The generic tokens (`bot`, `crawl`, `spider`) are broad by design,
 * which is correct here: a real browser's user-agent does not contain them, and
 * a false positive costs one uncounted visitor rather than a broken page.
 */
const AUTOMATION_UA_TOKENS = [
    // Generic self-identification.
    'bot',
    'crawl',
    'spider',
    'slurp',
    'scrap',
    'preview',
    // Headless and driver stacks.
    'headless',
    'phantom',
    'puppeteer',
    'playwright',
    'selenium',
    'webdriver',
    // HTTP clients and languages.
    'python',
    'curl',
    'wget',
    'java/',
    'go-http',
    'node-fetch',
    'axios',
    // Named vendors. Several of these do not match the generic tokens above, so
    // they have to be listed: Google-Read-Aloud, GoogleOther and
    // Google-InspectionTool all reach us and none contain "bot".
    'ahrefs',
    'semrush',
    'dataforseo',
    'hubspot',
    'facebookexternalhit',
    'externalagent',
    'externalads',
    'google-read-aloud',
    'googleother',
    'google-inspectiontool',
    'lighthouse',
    'gtmetrix',
    'pingdom',
    'screaming',
];
/** Brand strings that mean the Chromium build is running headless. */
const HEADLESS_BRAND_TOKENS = ['headless'];
/**
 * Whether `navigator.userAgentData.brands` names a headless build.
 *
 * Read defensively: `userAgentData` is Chromium-only, and the surfaces run on
 * arbitrary dealer pages where a host script may have replaced `navigator`
 * members outright. Any unexpected shape reads as "not detected" rather than
 * throwing into the caller's init path.
 */
function hasHeadlessBrand(nav) {
    const brands = nav.userAgentData?.brands;
    if (!Array.isArray(brands))
        return false;
    return brands.some((entry) => {
        const brand = typeof entry?.brand === 'string' ? entry.brand.toLowerCase() : '';
        return HEADLESS_BRAND_TOKENS.some((token) => brand.includes(token));
    });
}
/** Whether the user-agent string names a crawler or automation tool. */
function hasAutomationUserAgent(userAgent) {
    const ua = userAgent.toLowerCase();
    return AUTOMATION_UA_TOKENS.some((token) => ua.includes(token));
}
/**
 * Evaluate the current runtime for automation. Safe to call before consent: it
 * reads already-sent request headers and standard navigator properties, stores
 * nothing, and sets no cookie.
 *
 * Returns `isBot: false` outside a browser, so server-side rendering and tests
 * are never classified as automation.
 */
function detectBot() {
    if (typeof navigator === 'undefined')
        return { isBot: false, signals: [] };
    const nav = navigator;
    const signals = [];
    if (hasHeadlessBrand(nav))
        signals.push('headlessBrand');
    if (typeof nav.userAgent === 'string' && hasAutomationUserAgent(nav.userAgent)) {
        signals.push('automationUserAgent');
    }
    if (nav.webdriver === true)
        signals.push('webdriver');
    return { isBot: signals.length > 0, signals };
}
/**
 * Memoised `detectBot`. The answer cannot change within a page load, and the
 * surfaces ask more than once (each CDP sink checks independently), so the
 * result is computed once and reused.
 */
let cached = null;
function isBotRuntime() {
    cached ??= detectBot();
    return cached.isBot;
}

export { detectBot, isBotRuntime };
//# sourceMappingURL=bot-detection.js.map
